import { Col, Row } from "react-bootstrap"
import React from "react"
import { CompetitionPageHeader } from "@components/Competition/CompetitionPageHeader/CompetitionPageHeader"
import { CompetitionTeams } from "@components/Competition/CompetitionTeams/CompetitionTeams"
import { TeamAPI } from "../../../../../src/apis/TeamAPI"
import { SeasonAPI } from "../../../../../src/apis/SeasonAPI"
import { CompetitionAPI } from "../../../../../src/apis/CompetitionAPI"
import { getNavigationProps } from "@components/Navigation/getNavigationProps"
import { RSS, SEO } from "../../../../../src/types/next.js"
import InferNextPropsType from "infer-next-props-type"
import { ParsedUrlQuery } from "querystring"
import { GetStaticPropsContext } from "next"
import * as NextConstants from "constants/next"
import { CompetitionConstants } from "../../../../../src/constants/competitions"
import { getCompetitionCanonicalData } from "../../../../../src/utils/competition"
import { NewTitleBanner } from "@components/Banners/NewTitleBanner"
import { CompetitionSeasonSelector } from "@components/Competition/CompetitionSeasonSelector/CompetitionSeasonSelector"
import { BookmakerBonusAPI } from "apis/BookmakerBonusAPI"
import { MatchAPI } from "../../../../../src/apis/MatchAPI"
import { simplifyBookmakerBonus } from "../../../../../src/utils/bookmakerBonus"

export default function CompetitionPage({
  seasonCalciocomSlug,
  competitionCalciocomSlug,
  seasons,
  competitionDetail,
  teams,
  headerTitle,
  hasPlayedMatch,
}: InferNextPropsType<typeof getStaticProps>) {
  return (
    <>
      <Row className={"marginZero"}>
        <div className={"hs-border-div paddingZero"}>
          <CompetitionPageHeader
            selected="squadre"
            competitionCalciocomSlug={competitionCalciocomSlug}
            competition={competitionDetail}
            showAll={hasPlayedMatch}
          />
          <div className="new-squadre-container">
            <NewTitleBanner title={headerTitle === undefined ? "Squadre" : headerTitle} />
            <div className="campionato-new-container new-squadre-tab">
              <Row>
                <CompetitionSeasonSelector
                  competitionCalciocomSlug={competitionCalciocomSlug}
                  seasonCalciocomSlug={seasonCalciocomSlug}
                  seasons={seasons}
                  page={"squadre"}
                />
              </Row>
              <CompetitionTeams
                competitionCalciocomSlug={competitionCalciocomSlug}
                seasonCalciocomSlug={seasonCalciocomSlug}
                teams={teams}
              />
            </div>
          </div>
        </div>
      </Row>
    </>
  )
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" }
}

export interface IParams extends ParsedUrlQuery {
  competitionCalciocomSlug: string
  seasonCalciocomSlug: string
}

export async function getStaticProps(context: GetStaticPropsContext<IParams>, index = false) {
  try {
    const { competitionCalciocomSlug, seasonCalciocomSlug } = context.params!

    const [competitionIds, seasonIds] = await Promise.all([
      CompetitionAPI.getCompetitionIds(competitionCalciocomSlug),
      SeasonAPI.getSeasonIds(seasonCalciocomSlug),
    ])
    const competition = await CompetitionAPI.getCompetition(competitionIds.id)

    const seasons = await SeasonAPI.getSeasonsByCompetitionId(competitionIds.id)
    const season = seasons.find((season) => season.id === seasonIds.id)!

    const teams = (await TeamAPI.getTeams(competitionIds.id, seasonIds.id)).sort((a, b) => a.name.localeCompare(b.name))

    const title = `${competition.name} ${season.name}: tutte le squadre partecipanti`
    const turnMatches = (await MatchAPI.getPlayedMatches(competitionIds.id, season.id)).slice(-10)

    const seo: SEO = {
      title: title,
      description: `Lista completa delle squadre partecipanti al Campionato di calcio di ${competition.name} nella stagione ${season.name}, con i dati statistici completi raccolti per squadra.`,
      keywords: [
        `${competition.name} ${season?.name}`,
        "Squadre",
        "calcio",
        "Giocatori",
        "Rosa",
        ...teams.map((t) => t.name),
      ],
      ...(await getCompetitionCanonicalData(
        competition,
        season,
        null,
        null,
        `/campionato/${competitionCalciocomSlug}/stagione/${seasonCalciocomSlug}/squadre`,
      )),
      canonicals: [`/campionato/${competitionCalciocomSlug}/squadre`],
      index: index,
    }

    let rss: RSS | null = null
    if (CompetitionConstants.TOP_COMPETITIONS_IDS.includes(competition.id)) {
      rss = {
        competitionCalcioComSlug: competitionCalciocomSlug,
      }
    }
    const competitionDetail = await CompetitionAPI.getLastSeasonCompetitionDetails(competitionIds.id)

    return {
      props: {
        competitionCalciocomSlug,
        seasonCalciocomSlug,
        seasons,
        teams,
        competitionDetail,
        navigationProps: await getNavigationProps(competitionIds.id),
        title,
        seo,
        rss,
        headerTitle: `Squadre ${competition.name} ${season?.name}`,
        hasPlayedMatch: turnMatches.length > 0,
      },
      revalidate: NextConstants.REVALIDATE,
    }
  } catch (error) {
    console.error(error)
    return {
      notFound: true,
    }
  }
}
